import { FolderIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CameraTagChip from '../CameraTagChip';
import { PTZ_FILTER_LABEL, type SelectedCameraTag } from './cameraTagsApi';

/**
 * The toolbar's "what is shown": the camera count, then one removable chip per active filter.
 * Shared by both dashboards' camera lists, beside their filter column.
 */
export interface CameraFilterChipsProps {
  resultCount: number | null;
  search: string;
  onClearSearch: () => void;
  groups: Array<{ id: string; name: string }>;
  onRemoveGroup: (group: any) => void;
  tags: SelectedCameraTag[];
  onRemoveTag: (tag: SelectedCameraTag) => void;
  ptz: string;
  onClearPtz: () => void;
  onClearAll: () => void;
}

export default function CameraFilterChips({
  resultCount, search, onClearSearch, groups, onRemoveGroup, tags, onRemoveTag, ptz, onClearPtz, onClearAll,
}: CameraFilterChipsProps) {
  const activeCount = (search ? 1 : 0) + groups.length + tags.length + (ptz ? 1 : 0);
  const removeBtn = (label: string, onClick: () => void) => (
    <button type="button" onClick={onClick} aria-label={`Remove ${label} from the filter`} className="rounded hover:opacity-70">
      <XMarkIcon className="h-3 w-3" />
    </button>
  );

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      <span className="mr-1 text-[13px] font-semibold text-foreground">
        {resultCount !== null ? `${resultCount} camera${resultCount === 1 ? '' : 's'}` : 'Cameras'}
      </span>
      {activeCount === 0 && <span className="text-[12px] text-muted-foreground">· no filters</span>}
      {search && (
        <CameraTagChip name={`“${search}”`} color="slate" selected>
          {removeBtn('the search', onClearSearch)}
        </CameraTagChip>
      )}
      {groups.map((g) => (
        <span key={g.id} className="inline-flex max-w-full items-center gap-1 rounded-md border border-brand bg-brand-light px-1.5 py-0.5 text-[11px] font-medium text-brand">
          <FolderIcon className="h-3 w-3 shrink-0" />
          <span className="truncate">{g.name}</span>
          {removeBtn(g.name, () => onRemoveGroup(g))}
        </span>
      ))}
      {ptz && (
        <CameraTagChip name={PTZ_FILTER_LABEL[ptz] || ptz} color="purple" selected>
          {removeBtn('the PTZ filter', onClearPtz)}
        </CameraTagChip>
      )}
      {tags.map((t) => (
        <CameraTagChip key={t.id} name={t.name} prefix={t.category_name} color={t.color} selected>
          {removeBtn(t.name, () => onRemoveTag(t))}
        </CameraTagChip>
      ))}
      {activeCount > 1 && (
        <button type="button" onClick={onClearAll} className="ml-1 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
          Clear all
        </button>
      )}
    </div>
  );
}
